import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import arraySort from 'array-sort'
import InputLabel from '../../typography/Input/InputLabel'
import NotYetSpecified from '../../mini/NotYetSpecified'
import colors from '../../../configuration/colors'
import _EditIcon from '../../../static/images/mycourses/Edit.svg'
import _EditGrayIcon from '../../../static/images/mycourses/EditGray.svg'
import _ArtifactIcon from '../../../static/images/Icon_Artifact.svg'
import _CheckInIcon from '../../../static/images/CheckIn.svg'
import PropTypes from 'prop-types'
import { getTheActivityType } from './getTheActivityTypeFunction'
import MarkdownLabel from '../../mini/MarkdownLabel'
import FlexAlignCenter from '../../presentational/FlexAlignCenter'
import FileTypeIcon from '../../mini/FileTypeIcon'
import ResourceCard from './ResourceCard'
import LinkedSkillsReadonly from './LinkedSkillsReadOnly'

const AsteriskSymbol = styled.span`
  color: ${colors.crimson};
  padding-left: 3px;
`

const EditIcon = styled.div`
  background-image: url(${props => (props.gray ? _EditGrayIcon : _EditIcon)});
  height: 23px;
  width: 23px;
  background-repeat: no-repeat;
  margin-left: 10px;
  margin-bottom: 10px;
`

const ArtifactIcon = styled.div`
  background-image: url(${_ArtifactIcon});
  height: 27px;
  width: 27px;
  margin-right: 11px;
  background-repeat: no-repeat;
`

const CheckInIcon = styled.div`
  background-image: url(${_CheckInIcon});
  height: 27px;
  width: 27px;
  background-repeat: no-repeat;
`

const Text = styled.div`
  color: ${colors.victoria};
  font-size: 16px;
  letter-spacing: 0;
`

const FileName = styled.a`
  color: ${colors.cobalt};
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 21px;
`

const FileDescription = styled.div`
  color: ${colors.black};
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 18px;
  margin: 7px 0 15px 42px;
`

const ReadOnlyActivityData = ({
  activity,
  mentorFiles = [],
  resources = [],
  linkedSkills = [],
  handleClick,
  isDraft
}) => {
  const artifactTypes = useSelector(state => state.artifacttypes.items)
  const activityType = getTheActivityType(activity).label

  const displayActivityTypeIcon = activityType => {
    let iconWithText
    switch (activityType) {
      case 'Artifact Required / Check-in': {
        iconWithText = (
          <FlexAlignCenter>
            <ArtifactIcon /> Artifact Required
            <span style={{ marginLeft: '11px' }}>/</span>
            <CheckInIcon style={{ marginLeft: '11px' }} /> Check-in
          </FlexAlignCenter>
        )
        break
      }
      case 'Check-in Only': {
        iconWithText = (
          <FlexAlignCenter>
            <CheckInIcon />
            Check-in Only
          </FlexAlignCenter>
        )
        break
      }
      case 'No Check-in': {
        break
      }
    }
    return iconWithText
  }

  const listOfMentorFiles = arraySort(mentorFiles, 'sort_order').map(
    mentorFile => {
      return (
        <div>
          <FlexAlignCenter key={mentorFile.id}>
            <FileTypeIcon type={mentorFile.file_type} />
            <FileName href={mentorFile.attachment_url} target="_blank">
              {mentorFile.original_file_name}
            </FileName>
          </FlexAlignCenter>
          <FileDescription>{mentorFile.description}</FileDescription>
        </div>
      )
    }
  )

  const evenResources = arraySort(resources, 'sort_order').filter(
    (resource, index) => index % 2 === 0
  )
  const oddResources = arraySort(resources, 'sort_order').filter(
    (resource, index) => index % 2 !== 0
  )

  const listOfEvenResources = evenResources.map(resource => {
    return <ResourceCard resource={resource} />
  })

  const listOfOddResources = oddResources.map(resource => {
    return <ResourceCard resource={resource} />
  })

  return (
    <div>
      <div style={{ cursor: 'pointer', float: 'right' }}>
        <EditIcon onClick={handleClick} gray={!isDraft} />
      </div>
      <FlexAlignCenter>{displayActivityTypeIcon(activityType)}</FlexAlignCenter>
      <InputLabel>
        Driving Question
        <AsteriskSymbol>*</AsteriskSymbol>
      </InputLabel>
      {!activity.driving_question ? (
        <NotYetSpecified />
      ) : (
        <Text>{activity.driving_question}</Text>
      )}
      <InputLabel>
        Activity Goals
        <AsteriskSymbol>*</AsteriskSymbol>
      </InputLabel>
      {!activity.goals ? (
        <NotYetSpecified />
      ) : (
        <Text>{activity.goals}</Text>
      )}
      {activity.work_product && activity.checkpoint && (
        <React.Fragment>
          <InputLabel>
            Artifact Type
            <AsteriskSymbol>*</AsteriskSymbol>
          </InputLabel>
          {!activity.artifact_type_id ? (
            <NotYetSpecified />
          ) : (
            <Text>
              {artifactTypes.find(at => at.id === activity.artifact_type_id)
                .name}
            </Text>
          )}
          <InputLabel>
            Artifact Description
            <AsteriskSymbol>*</AsteriskSymbol>
          </InputLabel>
          {!activity.artifact_description ? (
            <NotYetSpecified />
          ) : (
            <Text>
              {activity.artifact_description !== null &&
                activity.artifact_description}
            </Text>
          )}
        </React.Fragment>
      )}

      <InputLabel>
        Learning Skills (Strongly Recommended)
      </InputLabel>
      {linkedSkills.length > 0 ? (
        <LinkedSkillsReadonly linkedSkills={linkedSkills} />
      ) : (
        <NotYetSpecified />
      )}

      <InputLabel>
        Description
        <AsteriskSymbol>*</AsteriskSymbol>
      </InputLabel>
      {!activity.description ? (
        <NotYetSpecified />
      ) : (
        <Text>
          <MarkdownLabel value={activity.description} />
        </Text>
      )}
      <InputLabel>Activity Resources</InputLabel>
      {resources.length > 0 ? (
        <div
          style={{
            display: 'flex',
            width: '80%',
            alignItems: 'flex-start',
            marginTop: '10px'
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', width: '50%' }}
          >
            {listOfEvenResources}
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', width: '50%' }}
          >
            {listOfOddResources}
          </div>
        </div>
      ) : (
        <NotYetSpecified />
      )}
      <div
        id="mentorNoteRegion"
        style={{
          backgroundColor: colors.smoke,
          borderRadius: '5px',
          padding: '18px',
          marginTop: '15px'
        }}
      >
        <InputLabel style={{ marginTop: '0' }}>Mentor Notes</InputLabel>
        {!activity.mentor_notes ? (
          <NotYetSpecified />
        ) : (
          <Text>
            <MarkdownLabel value={activity.mentor_notes} />
          </Text>
        )}
        <InputLabel>Mentor Files</InputLabel>
        {mentorFiles.length === 0 ? (
          <NotYetSpecified />
        ) : (
          <div style={{ marginTop: '15px' }}>{listOfMentorFiles}</div>
        )}
        <InputLabel>Assessment Questions</InputLabel>
        {!activity.mentor_assessment_questions ? (
          <NotYetSpecified />
        ) : (
          <div style={{ marginBottom: '-4px' }}>
            <Text>
              <MarkdownLabel value={activity.mentor_assessment_questions} />
            </Text>
          </div>
        )}
      </div>
    </div>
  )
}

ReadOnlyActivityData.propTypes = {
  activity: PropTypes.object.isRequired,
  mentorFiles: PropTypes.array.isRequired,
  resources: PropTypes.array.isRequired,
  handleClick: PropTypes.func,
  isDraft: PropTypes.bool.isRequired
}

export default ReadOnlyActivityData



// WEBPACK FOOTER //
// ./src/components/mycourses/Activities/ReadOnlyActivityData.js